import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { BufferedChangeset } from 'ember-changeset/types';
import { restartableTask } from 'ember-concurrency';
import IntlService from 'ember-intl/services/intl';
import PlanningModel from 'volta/models/planning';
import PlanningSetting from 'volta/models/planning-setting';
import Simulation from 'volta/models/simulation';
import SimulationItemSetting from 'volta/models/simulation-item-setting';
import SkuModel from 'volta/models/sku';
import { ISimulatedBuffer } from 'volta/models/types/planning';
import UpdatePlanningSettingsCmd from 'volta/models/update-planning-settings-cmd';
import AppSettings from 'volta/services/app-settings';
import AuthorizationService from 'volta/services/authorization';
import BvFlashService from 'volta/services/bv-flash';
import EventStreamService from 'volta/services/event-stream';
import StoreService from 'volta/services/store';
import { DocWithData } from 'volta/utils/api/jsonapi-types';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler, IAPIError } from 'volta/utils/error-utils';
import { round } from 'volta/utils/math-utils';
import PlanningSettingsValidations from 'volta/validations/update-planning-settings';

import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { TEditorKey } from '../forms/planning-settings-editor';

interface IArgs {
  /* Sku model */
  sku: SkuModel;
  /* Initial Planning settings */
  settings: PlanningSetting;
  /* This is used only when the component is called in the simulation context (`@inSimulationModule === true`) */
  itemSetting?: SimulationItemSetting;
  /* Whether the component is used in the simulation context */
  inSimulationModule: boolean;
  /**
   * Called when the user click on the save button,
   * the actual API call is done in the parent component because
   * it could be either a SkuDetails::Settings or in the simulation module
   */
  onSave?: (
    settings: PlanningSetting,
    cmd: UpdatePlanningSettingsCmd
  ) => Promise<PlanningSetting> | void;
  /* Optional callback on save success */
  onSaveSuccess?: () => void;
  /* Optional callback called when any value changes */
  onChange?: (cs: BufferedChangeset) => void;
}
export default class PlanningSettings extends Component<IArgs> {
  // Services
  // ~~~~~

  @service appSettings!: AppSettings;
  @service authorization!: AuthorizationService;
  @service bvFlash!: BvFlashService;
  @service eventStream!: EventStreamService;
  @service intl!: IntlService;
  @service router!: RouterService;
  @service store!: StoreService;

  // Properties
  // ~~~~~

  @tracked settings!: PlanningSetting;
  @tracked settingsChangeset!: BufferedChangeset;
  @tracked updateSettingsCmd!: UpdatePlanningSettingsCmd;
  @tracked showNewSimulationModal: boolean = false;
  @tracked showImpactedFP: boolean = false;
  @tracked impactedFP: SkuModel[] = [];
  @tracked selectedImpactedFP: SkuModel[] = [];
  @tracked showSaveConfirmModal: boolean = false;
  @tracked showSettingsSuggestionModal: boolean = false;
  @tracked isSaving: boolean = false;
  @tracked simulatedBuffer?: ISimulatedBuffer;
  @tracked realBuffer?: ISimulatedBuffer;
  @tracked bufferSimulationLoading: boolean = false;
  @tracked skuIdsForSimulation: string[] = [];

  // Computed Properties
  // ~~~~~

  get isMrpValid() {
    return (
      isPresent(this.settingsChangeset.get('settings.demandHorizon')) ||
      this.settingsChangeset.methodology !== 'MRP'
    );
  }

  get enableBufferSimulation() {
    return !this.args.inSimulationModule && this.authorization.can('plannings', 'SimulateBuffer');
  }

  get suggestedAduMin() {
    const adu = this.simulatedBuffer?.adu ?? this.realBuffer?.adu ?? 0;
    const lt = this.args.sku?.actualLeadTime;
    return round(
      lt ? adu + (adu * (this.settingsChangeset.get('settings.orderCycle') ?? 1)) / lt : adu
    );
  }

  get suggestedFixedBufferZones() {
    return {
      redBase: round(this.simulatedBuffer?.buffer.redBase ?? this.realBuffer?.buffer.redBase),
      redSafety: round(this.simulatedBuffer?.buffer.redSafety ?? this.realBuffer?.buffer.redSafety),
      yellow: round(this.simulatedBuffer?.buffer.yellow ?? this.realBuffer?.buffer.yellow),
      green: round(this.simulatedBuffer?.buffer.green ?? this.realBuffer?.buffer.green)
    };
  }

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.initChangeset(this.args.settings);
  }

  @action
  simulateBuffer(simulated: boolean = true) {
    if (this.enableBufferSimulation && this.settings.settings.methodology === 'BUFFER') {
      this.simulateBufferTask.perform(simulated);
    }
  }

  @action
  handleSkuChange() {
    this.simulateBuffer(false);
  }

  // Actions
  // ~~~~~

  @action
  addImpactedFPToSimulation() {
    const skuIds = this.selectedImpactedFP.map((sku) => sku.id);

    if (!skuIds || !skuIds.length) {
      return;
    }

    this.skuIdsForSimulation = skuIds;
    this.showNewSimulationModal = true;
  }

  @action
  handleShowImpactedFP() {
    this.fetchImpactedFP.perform();
    this.showImpactedFP = true;
  }

  @action
  closeImpactedFPModal() {
    this.selectedImpactedFP = [];
    this.showImpactedFP = false;
  }

  @action
  closeNewSimulationModal() {
    this.showNewSimulationModal = false;
    this.skuIdsForSimulation = [];
  }

  @action
  onCreateSimulationSuccess(simulation: Simulation) {
    this.showNewSimulationModal = false;
    this.skuIdsForSimulation = [];
    this.router.transitionTo('protected.simulation', simulation.id);
  }

  @action
  save() {
    this.showSaveConfirmModal = false;
    this.submitCmd();
  }

  @action
  handleSettingsUpdate(_key: TEditorKey, cs: BufferedChangeset) {
    this.settingsChangeset = cs;
    this.simulateBuffer(true);
    return this.args.onChange?.(this.settingsChangeset);
  }

  // Helper Functions
  // ~~~~~

  submitCmd() {
    const cs: BufferedChangeset = this.settingsChangeset;
    cs.validate();

    if (cs.isValid && this.isMrpValid) {
      cs.execute();
      this.isSaving = true;

      // Here the onSave function could refer to the planning settings model or the simulation item settings
      const result: Promise<PlanningSetting> | void = this.args.onSave?.(
        this.settings,
        this.updateSettingsCmd
      );

      if (result && typeof result.then === 'function') {
        return result.then((settings: PlanningSetting) => {
          this.initChangeset(settings);
          if (this.enableBufferSimulation && this.settings.settings.methodology === 'BUFFER') {
            this.simulateBufferTask.perform(false);
          }
          this.isSaving = false;
          return settings;
        });
      }
    }
    return;
  }

  @action
  initChangeset(settings?: PlanningSetting) {

    const ps =
      settings ??
      new PlanningSetting({
        skuId: this.args.sku.id
      });

    // Here we default the demand horizon to the SKU lead time if not set
    if (!isPresent(ps.settings.demandHorizon)) {
      ps.settings.demandHorizon = this.args.sku.actualLeadTime ?? 0;
    }

    this.settings = ps;

    if (!ps) {
      return;
    }

    this.updateSettingsCmd = new UpdatePlanningSettingsCmd(ps);
    this.settingsChangeset = Changeset(
      this.updateSettingsCmd,
      lookupValidator(PlanningSettingsValidations),
      PlanningSettingsValidations
    );
  }

  fetchImpactedFP = restartableTask(async () => {
    try {
      const response: DocWithData = await this.store.customQuery(
        'sku',
        'impactedFP',
        this.args.sku.id
      );
      this.impactedFP = normalize('sku', response) as SkuModel[];
      return this.impactedFP;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });

  simulateBufferTask = restartableTask(async (isSimulated?: boolean) => {
    try {
      const { sku } = this.args;
      const cs = this.settingsChangeset;
      const params = {
        settings: { ...(cs.pendingData as UpdatePlanningSettingsCmd).settings },
        skuId: sku?.skuId,
        leadTime: sku?.actualLeadTime,
        minOrderQty: sku?.minOrderQty,
        orderMultiple: sku?.orderMultiple
      };

      const response: { data: ISimulatedBuffer } = await PlanningModel.simulateBuffer(params);

      if (isSimulated) {
        this.simulatedBuffer = response.data;
      } else {
        this.realBuffer = response.data;
        this.simulatedBuffer = undefined;
      }

      return response;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  // Private Callbacks
  // ~~~~~

  _onDefaultError(reason: IAPIError) {
    defaultErrorHandler(reason);
    this.bvFlash.apiError(reason);
  }
}
